import React from 'react'
import { Grid, Typography } from '@mui/material'
import { faCalculator } from '@fortawesome/pro-regular-svg-icons'
import { SectionWrapper } from '../../../../common/components/SectionWrapper/SectionWrapper'
import {
  DetailRow,
  DetailInfo,
} from '../../../../common/components/DetailRow/DetailRow'
import { dateAndTimeConversion } from '../../../../common/utils/dateAndTimeConversion'
import { getContractDuration } from '../subscriptionDetailHelpers'
import { BoxV4, LabraToolTipV2, PlanType } from '@labrav/react-components'
import { PRICING_CONFIG_COLLAPSIBLE_STYLE_V2 } from '../SubscriptionDetail.styles'
import { renderSubscriptionDetailHeadingIcon } from './renderSubscriptionDetailHeadingIcon'
import {
  SubscriptionDetailObject,
  EntitlementSubs,
} from '../../../../modules/subscription/reducer'
import { labravDefaultThemeV7 as theme } from '@labrav/react-components'
import { ShortenedNameV2 } from '@labrav/common'
import { ColumnReadMore } from '@labrav/common'
import { ViewValueWithTruncate } from '@labrav/common'

interface PricingConfigurationSectionProps {
  subscriptionDetail: SubscriptionDetailObject
  cloudType: string
  timeZone: string
  classes: any
}

export const PricingConfigurationSection: React.FC<
  PricingConfigurationSectionProps
> = ({ subscriptionDetail, cloudType, timeZone, classes }) => {
  const awsPriceSymbol = (currencySymbol?: string | null) =>
    cloudType === 'AWS'
      ? currencySymbol ||
        subscriptionDetail.subscription?.currencySymbol ||
        '$'
      : '$'

  if (cloudType === 'GCP' && subscriptionDetail) {
    const pc = subscriptionDetail.pricingConfiguration
    const md = subscriptionDetail.meteringDimensions || []
    if (!pc) return null
    const contractDurationLabel =
      pc.contractDuration != null ? `${pc.contractDuration} months` : '-'

    const descriptionFull = (pc.description || '').trim()
    const featuresJoined =
      pc.features && pc.features.length > 0
        ? pc.features
            .map(f => f.value || f.label || '')
            .filter(Boolean)
            .join(', ')
        : ''

    return (
      <div className={classes.pricingConfigBoxWrapper}>
        <BoxV4
          variant="small"
          headingVariant="small"
          heading="Pricing configuration"
          headingIcon={renderSubscriptionDetailHeadingIcon(classes)}
          collapsible
          collapsibleStyle={PRICING_CONFIG_COLLAPSIBLE_STYLE_V2}
        >
          <div className={classes.pricingConfigBodyInner}>
            <div className={classes.pricingConfigContent}>
              <div className={classes.pricingSectionV2}>
                <div className={classes.pricingTitle}>Plan</div>
                <div className={classes.billingRow}>
                  <div className={classes.billingColumnV2}>
                    <div className={classes.billingLabel}>Name</div>
                    <div className={classes.billingValueV2}>
                      {pc.planName || '-'}
                    </div>
                  </div>
                  <div className={classes.billingColumnV2}>
                    <div className={classes.billingLabel}>Description</div>
                    {descriptionFull ? (
                      <LabraToolTipV2
                        tooltipPlacement="top"
                        tooltipPlacmentGap="12px"
                        description={pc.description || ''}
                      >
                        <div className={classes.billingValueV2}>
                          {pc.description}
                        </div>
                      </LabraToolTipV2>
                    ) : (
                      <div className={classes.billingValueV2}>-</div>
                    )}
                  </div>
                  <div className={classes.billingColumnV2}>
                    <div className={classes.billingLabel}>Features</div>
                    {featuresJoined ? (
                      <LabraToolTipV2
                        tooltipPlacement="top"
                        tooltipPlacmentGap="12px"
                        description={featuresJoined}
                      >
                        <div className={classes.billingValueV2}>
                          {featuresJoined}
                        </div>
                      </LabraToolTipV2>
                    ) : (
                      <div className={classes.billingValueV2}>-</div>
                    )}
                  </div>
                </div>
              </div>
              <div
                className={classes.pricingSectionSeparator}
                role="separator"
                aria-hidden
              >
                <div className={classes.pricingSectionSeparatorLine} />
              </div>
              <div className={classes.pricingSectionV2}>
                <div className={classes.pricingTitle}>Billing terms</div>
                <div className={classes.billingRow}>
                  <div className={classes.billingColumnV2}>
                    <div className={classes.billingLabel}>
                      Contract duration
                    </div>
                    <div className={classes.billingValueV2}>
                      {contractDurationLabel}
                    </div>
                  </div>
                  <div className={classes.billingColumnV2}>
                    <div className={classes.billingLabel}>Payment schedule</div>
                    <div className={classes.billingValueV2}>
                      {pc.billingTerms || '-'}
                    </div>
                  </div>
                  <div className={classes.billingColumnV2}>
                    <div className={classes.billingLabel}>Monthly flat fee</div>
                    <div className={classes.billingValueV2}>
                      {pc.monthlyListFee != null ? (
                        <>
                          <span className={classes.currencySymbol}>$</span>
                          {pc.monthlyListFee.toLocaleString()}
                        </>
                      ) : (
                        '-'
                      )}
                    </div>
                  </div>
                  <div className={classes.billingColumnV2}>
                    <div className={classes.billingLabel}>
                      Total contract value
                    </div>
                    <div className={classes.billingValueV2}>
                      {pc.totalContractValue != null ? (
                        <>
                          <span className={classes.currencySymbol}>$</span>
                          {pc.totalContractValue.toLocaleString()}
                        </>
                      ) : (
                        '-'
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {md.length > 0 && (
                <div
                  className={classes.billingMeteringSeparator}
                  role="separator"
                  aria-hidden
                />
              )}
              {md.length > 0 && (
                <div className={classes.pricingSectionV2}>
                  <div className={classes.meteringBox}>
                    <div className={classes.pricingTitle}>
                      Metering dimensions
                    </div>
                    <div className={classes.meteringTableV2}>
                      <div className={classes.meteringHeaderV2}>
                        <div className={classes.meteringHeaderCellV2}>Name</div>
                        <div className={classes.meteringHeaderCellV2}>
                          Discount
                        </div>
                        <div className={classes.meteringHeaderCellV2}>
                          Discounted price/unit
                        </div>
                        <div className={classes.meteringHeaderCellV2}>Date</div>
                      </div>
                      {md.map((dim, index) => (
                        <div key={index} className={classes.meteringRowV2}>
                          <div className={classes.meteringCellV2}>
                            <ShortenedNameV2
                              name={dim.name || '-'}
                              length={20}
                            />
                          </div>
                          <div className={classes.meteringCellV2}>
                            {dim.discount != null ? `${dim.discount}%` : '-'}
                          </div>
                          <div className={classes.meteringCellV2}>
                            {dim.discountedPricePerUnit != null ? (
                              <>
                                <span className={classes.currencySymbol}>$</span>
                                {dim.discountedPricePerUnit.toLocaleString()}
                              </>
                            ) : (
                              '-'
                            )}
                          </div>
                          <div className={classes.meteringCellV2}>
                            {dim.date
                              ? dateAndTimeConversion(dim.date, 'UTC')
                                  .modifiedDate
                              : '-'}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </BoxV4>
      </div>
    )
  }

  if (cloudType === 'AWS' && subscriptionDetail.entitlements.length === 0) {
    return (
      <SectionWrapper heading="Pricing configuration" icon={faCalculator}>
        <Typography
          variant="body2"
          style={{
            textAlign: 'center',
            color: theme.palette.labraText.gray,
            margin: `${theme.spacing(3)} 0`,
          }}
          data-testid="no-entitlements-message"
        >
          No records to show
        </Typography>
      </SectionWrapper>
    )
  }

  return (
    <SectionWrapper heading="Pricing configuration" icon={faCalculator}>
      <>
        {cloudType === 'AZURE' && (
          <div className={classes.pricingSection}>
            <div className={classes.pricingTitle}>Plan</div>
            <Grid container spacing={1} className={classes.billingTermsRow}>
              <Grid size={4}>
                <div className={classes.billingColumn}>
                  <div className={classes.billingLabel}>Name</div>
                  <div className={classes.billingValue}>
                    <ViewValueWithTruncate
                      value={subscriptionDetail?.planDetails?.displayName}
                    />
                  </div>
                </div>
              </Grid>
              <Grid
                size={
                  subscriptionDetail.privateOfferPlanDetails?.discountPercentage
                    ? 4
                    : 8
                }
              >
                <div className={classes.billingColumn}>
                  <div className={classes.billingLabel}>Description</div>
                  <div className={classes.billingValue}>
                    <ColumnReadMore
                      description={
                        subscriptionDetail.planDetails?.description || ''
                      }
                    />
                  </div>
                </div>
              </Grid>

              {subscriptionDetail.privateOfferPlanDetails
                ?.discountPercentage ? (
                <Grid size={4}>
                  <div className={classes.billingColumn}>
                    <div className={classes.billingLabel}>
                      Discount percentage
                    </div>
                    <div className={classes.billingValue}>
                      {
                        subscriptionDetail.privateOfferPlanDetails
                          ?.discountPercentage
                      }
                    </div>
                  </div>
                </Grid>
              ) : (
                <></>
              )}
            </Grid>
          </div>
        )}
        {cloudType === 'AZURE' && (
          <div className={classes.pricingSection}>
            <div className={classes.pricingTitle}>Billing terms</div>
            <div className={classes.billingTermsRow}>
              <div className={classes.billingColumn}>
                <div className={classes.billingLabel}>Contract duration</div>
                <div className={classes.billingValue}>
                  {getContractDuration(subscriptionDetail?.subscription)}
                </div>
              </div>
              <div className={classes.billingColumn}>
                <div className={classes.billingLabel}>Payment terms</div>
                <div className={classes.billingValue}>
                  {subscriptionDetail.offerDetails.flexiblePaymentTerms
                    ? 'Flexible schedule'
                    : subscriptionDetail.planPricingDetails?.paymentTerms ||
                      '-'}
                </div>
              </div>
              <div className={classes.billingColumn}>
                <div className={classes.billingLabel}>Total contract value</div>
                <div className={classes.billingValue}>
                  {subscriptionDetail.subscription.tcv !== undefined &&
                  subscriptionDetail.subscription.tcv !== null ? (
                    <>
                      <span className={classes.currencySymbol}>$</span>
                      {subscriptionDetail.subscription.tcv.toLocaleString()}
                    </>
                  ) : (
                    '-'
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        {cloudType === 'AWS' &&
          subscriptionDetail.entitlements.filter(
            (entitlement: EntitlementSubs) =>
              !entitlement.isAdditionalUsageAllowed
          ).length > 0 && (
            <div className={classes.pricingSection}>
              {subscriptionDetail.productDetails.productType ===
                PlanType.SAAS_CONTRACT && (
                <>
                  <div className={classes.pricingTitle}>Dimensions</div>
                  <div className={classes.meteringTable}>
                    <div className={classes.meteringHeader}>
                      <div className={classes.meteringHeaderCell}>Name</div>
                      <div className={classes.meteringHeaderCell}>
                        Expiry date
                      </div>
                      <div className={classes.meteringHeaderCell}>Quantity</div>
                      <div className={classes.meteringHeaderCell}>Price</div>
                    </div>
                    {subscriptionDetail.entitlements
                      .filter(
                        (entitlement: EntitlementSubs) =>
                          !entitlement.isAdditionalUsageAllowed
                      )
                      .map((entitlement: EntitlementSubs, index: number) => (
                        <div key={index} className={classes.meteringRow}>
                          <div className={classes.meteringCell}>
                            <ShortenedNameV2
                              name={entitlement.dimensionLabel || '-'}
                              length={20}
                            />
                          </div>
                          <div className={classes.meteringCell}>
                            {entitlement.expirationDate
                              ? dateAndTimeConversion(
                                  entitlement.expirationDate,
                                  timeZone
                                ).modifiedDate
                              : '-'}
                          </div>
                          <div className={classes.meteringCell}>
                            {entitlement.value || '-'}
                          </div>
                          <div className={classes.meteringCell}>
                            {entitlement.price ? (
                              <>
                                <span className={classes.currencySymbol}>
                                  {awsPriceSymbol(entitlement.currencySymbol)}
                                </span>
                                {entitlement.price.toLocaleString()}
                              </>
                            ) : (
                              '-'
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                </>
              )}
              {subscriptionDetail.productDetails.productType ===
                PlanType.SAAS_SUBSCRIPTION && (
                <>
                  <div className={classes.pricingTitle}>Usage dimensions</div>
                  <div className={classes.meteringTable}>
                    <div className={classes.meteringHeader}>
                      <div className={classes.meteringHeaderCell}>Name</div>
                      <div className={classes.meteringHeaderCell}>Unit</div>
                      <div className={classes.meteringHeaderCell}>
                        Price per unit
                      </div>
                    </div>
                    {subscriptionDetail.entitlements
                      .filter(
                        (entitlement: EntitlementSubs) =>
                          !entitlement.isAdditionalUsageAllowed
                      )
                      .map((entitlement: EntitlementSubs, index: number) => (
                        <div key={index} className={classes.meteringRow}>
                          <div className={classes.meteringCell}>
                            <ShortenedNameV2
                              name={entitlement.dimensionLabel || '-'}
                              length={20}
                            />
                          </div>
                          <div className={classes.meteringCell}>
                            {entitlement.unit || '-'}
                          </div>
                          <div className={classes.meteringCell}>
                            {entitlement.price ? (
                              <>
                                <span className={classes.currencySymbol}>
                                  {awsPriceSymbol(entitlement.currencySymbol)}
                                </span>
                                {entitlement.price.toLocaleString()}
                              </>
                            ) : (
                              '-'
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                </>
              )}
            </div>
          )}
        {subscriptionDetail.entitlements.filter(
          (entitlement: EntitlementSubs) => entitlement.isAdditionalUsageAllowed
        ).length > 0 && (
          <div className={classes.pricingSection}>
            <div className={classes.pricingTitle}>Metering dimensions</div>
            <div className={classes.meteringTable}>
              <div className={classes.meteringHeader}>
                <div className={classes.meteringHeaderCell}>Name</div>
                <div className={classes.meteringHeaderCell}>Unit</div>
                <div className={classes.meteringHeaderCell}>Price per unit</div>
                {cloudType === 'AZURE' && (
                  <div className={classes.meteringHeaderCell}>
                    Base quantity
                  </div>
                )}
              </div>
              {subscriptionDetail.entitlements
                .filter(
                  (entitlement: EntitlementSubs) =>
                    entitlement.isAdditionalUsageAllowed
                )
                .map((entitlement: EntitlementSubs, index: number) => (
                  <div key={index} className={classes.meteringRow}>
                    <div className={classes.meteringValue}>
                      <ShortenedNameV2
                        name={entitlement.dimensionLabel || '-'}
                        length={20}
                      />
                    </div>
                    <div className={classes.meteringValue}>
                      {entitlement.unit || '-'}
                    </div>
                    <div className={classes.meteringValue}>
                      {entitlement.price ? (
                        <>
                          <span className={classes.currencySymbol}>
                            {awsPriceSymbol(entitlement.currencySymbol)}
                          </span>
                          {entitlement.price.toLocaleString()}
                        </>
                      ) : (
                        '-'
                      )}
                    </div>
                    {cloudType === 'AZURE' && (
                      <div className={classes.meteringCell}>
                        {entitlement.quantity || '-'}
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </div>
        )}
        {cloudType === 'AZURE' &&
          subscriptionDetail.planDetails?.minQuantity && (
            <>
              <div className={classes.pricingSection}>
                <div className={classes.pricingTitle}>Min-max users</div>
                <div className={classes.pricingContent}>
                  <DetailRow key={'min-max-users'} borderBottom={true}>
                    <>
                      <DetailInfo
                        title="Min-user"
                        value={
                          <ShortenedNameV2
                            name={
                              subscriptionDetail.planDetails?.minQuantity?.toString() ||
                              '-'
                            }
                            length={20}
                          />
                        }
                        keyName={`min-user`}
                      />
                      <DetailInfo
                        title="Max-user"
                        value={
                          <ShortenedNameV2
                            name={
                              subscriptionDetail.planDetails?.maxQuantity?.toString() ||
                              '-'
                            }
                            length={50}
                          />
                        }
                        keyName={`max-user`}
                      />
                    </>
                  </DetailRow>
                </div>
              </div>
            </>
          )}
      </>
    </SectionWrapper>
  )
}
